import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UserCheck, UserX, Sparkles, Users } from 'lucide-react';
import { format, isPast } from 'date-fns';
import useClients from '../hooks/useClients';
import useBookings from '../hooks/useBookings';

// ── Legacy slot helpers ───────────────────────────────────────────────────────

const MAX_SLOTS = 20;
const WEEKS = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
const DAYS  = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const toSlotId = (week, day) =>
  `week_${week.split(' ')[1]}_${day.toLowerCase()}`;

const slotLabel = (slotId) => {
  if (!slotId) return '—';
  const parts = slotId.split('_');
  const week = `Week ${parts[1]}`;
  const day  = parts[2]?.charAt(0).toUpperCase() + parts[2]?.slice(1, 3) || '';
  return `${week} · ${day}`;
};

// ── Interior Maintenance client table ─────────────────────────────────────────

function InteriorMaintSection({ resets, loading }) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#68caf1]" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#68caf1]" />
          <h3 className="font-semibold text-gray-800">Interior Maintenance Clients</h3>
        </div>
        <span className="text-xs text-gray-400">{resets.length} client{resets.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[700px]">
        <thead className="bg-gray-50 border-b border-gray-100">
          <tr>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Client</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Vehicle</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Reset Date</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Reset Total</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Monthly</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {resets.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                No interior maintenance clients yet.
              </td>
            </tr>
          ) : (
            resets.map(b => {
              const scheduledDate = b.scheduled_at
                ? (typeof b.scheduled_at === 'string' ? new Date(b.scheduled_at) : b.scheduled_at)
                : null;
              const past = scheduledDate ? isPast(scheduledDate) : false;
              const effectiveStatus = b.status === 'scheduled' && past ? 'completed' : b.status;

              return (
                <tr key={b.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {b.client_id ? (
                      <Link to={`/admin/clients/${b.client_id}`} className="font-medium text-gray-900 hover:text-[#68caf1]">
                        {b.client_name}
                      </Link>
                    ) : (
                      <span className="font-medium text-gray-900">{b.client_name}</span>
                    )}
                    <div className="text-xs text-gray-400">{b.email}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-gray-600">
                    {b.year && b.make ? `${b.year} ${b.make} ${b.model || ''}`.trim() : b.vehicle || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {scheduledDate ? format(scheduledDate, 'MMM d, yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-600">
                    {b.price ? `$${Number(b.price).toFixed(0)}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className="bg-[#68caf1]/10 text-[#1a7fa8] text-xs font-semibold px-2 py-1 rounded-lg whitespace-nowrap">
                      $75–$85/mo
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium w-fit inline-block ${
                      effectiveStatus === 'completed'
                        ? 'bg-emerald-100 text-emerald-800'
                        : effectiveStatus === 'scheduled'
                        ? 'bg-blue-100 text-blue-700'
                        : effectiveStatus === 'cancelled'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {effectiveStatus === 'scheduled' ? 'Upcoming Reset' : effectiveStatus || 'active'}
                    </span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ── Legacy slot grid ──────────────────────────────────────────────────────────

function LegacySlotGrid({ members, loading }) {
  const slotMap = useMemo(() => {
    const m = {};
    members.forEach(c => { if (c.slot) m[c.slot] = c; });
    return m;
  }, [members]);

  const activeCount = members.filter(m => m.status === 'active').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Users size={16} className="text-[#C9A84C]" />
        <h3 className="font-semibold text-gray-800">Legacy Enrollment Members</h3>
        <span className="text-xs text-gray-400 ml-auto">{activeCount} / {MAX_SLOTS} filled</span>
      </div>

      {/* Grid */}
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="text-left pb-3 pr-4 text-gray-400 font-medium text-xs w-20" />
                {DAYS.map(day => (
                  <th key={day} className="pb-3 px-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {day.slice(0, 3)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {WEEKS.map(week => (
                <tr key={week}>
                  <td className="py-1.5 pr-4 text-xs font-semibold text-gray-500 whitespace-nowrap">{week}</td>
                  {DAYS.map(day => {
                    const slotId = toSlotId(week, day);
                    const client = slotMap[slotId];
                    return (
                      <td key={slotId} className="py-1.5 px-2">
                        {client ? (
                          <Link
                            to={`/admin/clients/${client.id}`}
                            title={`${client.name} — ${client.year} ${client.make} ${client.model}`}
                            className="flex items-center justify-center rounded-xl bg-[#C9A84C] text-black hover:bg-[#b8943d] transition-colors cursor-pointer px-2 py-2 min-w-[72px] text-center"
                          >
                            <span className="text-[10px] font-bold leading-tight">{client.name}</span>
                          </Link>
                        ) : (
                          <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-200 text-gray-300 px-2 py-2 min-w-[72px]">
                            <span className="text-[10px]">Open</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-6 mt-4 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-[#C9A84C]" />
            <span>Filled</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded border-2 border-dashed border-gray-200" />
            <span>Available</span>
          </div>
          <span className="ml-auto text-[#C9A84C] font-semibold">
            {MAX_SLOTS - activeCount} of {MAX_SLOTS} spots open
          </span>
        </div>
      </div>

      {/* Legacy member table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" />
        </div>
      ) : members.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Slot</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Member</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Vehicle</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Joined</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Last Service</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {members.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="bg-[#C9A84C]/10 text-[#C9A84C] text-xs font-semibold px-2 py-1 rounded-lg whitespace-nowrap">
                      {slotLabel(c.slot)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/admin/clients/${c.id}`} className="font-medium text-gray-900 hover:text-[#C9A84C]">
                      {c.name}
                    </Link>
                    <div className="text-xs text-gray-400">{c.email}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-gray-600">
                    {c.year && c.make ? `${c.year} ${c.make} ${c.model || ''}` : '—'}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-500 text-xs">
                    {c.createdAt?.toDate ? format(c.createdAt.toDate(), 'MMM d, yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-500 text-xs">
                    {c.lastServiceDate
                      ? format(c.lastServiceDate.toDate ? c.lastServiceDate.toDate() : new Date(c.lastServiceDate), 'MMM d, yyyy')
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium w-fit ${
                      c.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {c.status === 'active' ? <UserCheck size={11} /> : <UserX size={11} />}
                      {c.status || 'active'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function MaintenanceProgram() {
  const { clients, loading: cLoading } = useClients();
  const { bookings, loading: bLoading } = useBookings();

  // New interior maintenance: bookings with booking_type = 'maintenance_reset'
  const interiorResets = useMemo(
    () => bookings.filter(b => b.booking_type === 'maintenance_reset'),
    [bookings]
  );

  // Legacy: clients with type = 'maintenance' AND a week_N_day slot
  const legacyMembers = useMemo(
    () => clients.filter(c => c.type === 'maintenance' && c.slot?.startsWith('week_')),
    [clients]
  );

  const totalActive = interiorResets.length + legacyMembers.filter(m => m.status === 'active').length;

  return (
    <div className="p-6 pb-24 md:pb-6 space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Maintenance Program</h2>
        <p className="text-gray-500 text-sm mt-0.5">
          {totalActive} total active client{totalActive !== 1 ? 's' : ''} across both programs
        </p>
      </div>

      {/* Section 1: Interior Maintenance (new flow) */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-px flex-1 bg-gray-100" />
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2">Interior Maintenance Program</span>
          <div className="h-px flex-1 bg-gray-100" />
        </div>
        <p className="text-xs text-gray-400 mb-3">Clients who booked the Alchemy Interior Reset + monthly maintenance</p>
        <InteriorMaintSection resets={interiorResets} loading={bLoading} />
      </div>

      {/* Section 2: Legacy enrollment slot grid */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-px flex-1 bg-gray-100" />
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2">Legacy Enrollment Members</span>
          <div className="h-px flex-1 bg-gray-100" />
        </div>
        <p className="text-xs text-gray-400 mb-3">Original recurring slot enrollments (Week 1–4 schedule)</p>
        <LegacySlotGrid members={legacyMembers} loading={cLoading} />
      </div>
    </div>
  );
}
